import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Group, Loader, Text, useDebouncedValue } from '@wapharma/design-system';
import { apiGet } from '../api';
import { unwrapData } from '@wapharma/shared';;
import { getToken } from '../auth';
import { SearchNormal1Twotone } from '../icons/mxNavTwotone';
import { MX_NAV_ICON } from '../lib/mxTwotoneIcon';
import { EntityTypeMark } from './EntityTypeMark';
import styles from './NavBarSearch.module.css';

type Federated = {
  products: Array<{ id: string; name: string; slug: string; status: string; reference?: string }>;
  orders: Array<{ id: string; reference: string | null; status: string }>;
};

type MeiliHit = Record<string, unknown>;

function productSearchLabel(name: string, reference?: string | null): string {
  const ref = String(reference ?? '')
    .trim()
    .split(/\s+/)[0];
  if (!ref) return name;
  return `${name} · #${ref}`;
}

type Suggestion =
  | { kind: 'product'; id: string; label: string; entityType: 'Product'; to: string }
  | { kind: 'order'; id: string; label: string; entityType: 'Order'; to: string };

function suggestionOptionId(s: Suggestion): string {
  return `nav-search-option-${s.kind}-${s.id}`;
}

function buildSuggestions(fed: Federated | null, meili: MeiliHit[]): Suggestion[] {
  const out: Suggestion[] = [];
  const seenProducts = new Set<string>();

  for (const h of meili) {
    const id = typeof h.id === 'string' ? h.id : String(h.id ?? '');
    if (!id || seenProducts.has(id)) continue;
    seenProducts.add(id);
    const name = typeof h.name === 'string' ? h.name : 'Produit';
    const ref = typeof h.reference === 'string' ? h.reference : null;
    out.push({
      kind: 'product',
      id,
      label: productSearchLabel(name, ref),
      entityType: 'Product',
      to: `/catalog/products/${encodeURIComponent(id)}`,
    });
    if (out.length >= 6) break;
  }

  if (fed) {
    for (const p of fed.products) {
      if (seenProducts.has(p.id)) continue;
      seenProducts.add(p.id);
      out.push({
        kind: 'product',
        id: p.id,
        label: productSearchLabel(p.name, p.reference ?? p.slug),
        entityType: 'Product',
        to: `/catalog/products/${encodeURIComponent(p.id)}`,
      });
      if (out.filter((s) => s.kind === 'product').length >= 8) break;
    }
    for (const o of fed.orders.slice(0, 5)) {
      out.push({
        kind: 'order',
        id: o.id,
        label: o.reference ?? o.id.slice(0, 8),
        entityType: 'Order',
        to: `/orders/${encodeURIComponent(o.id)}`,
      });
    }
  }

  return out.slice(0, 16);
}

export function NavBarSearch() {
  const token = getToken();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debounced] = useDebouncedValue(q.trim(), 280);
  const [fed, setFed] = useState<Federated | null>(null);
  const [meiliHits, setMeiliHits] = useState<MeiliHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  useEffect(() => {
    if (!token || !debounced) {
      setFed(null);
      setMeiliHits([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    void Promise.all([
      apiGet<{ data: Federated } | Federated>(`/search?q=${encodeURIComponent(debounced)}`, token)
        .then((body) => unwrapData<Federated>(body))
        .catch(() => null),
      apiGet<{ data: MeiliHit[] }>(`/search/products?q=${encodeURIComponent(debounced)}`, token)
        .then((body) => {
          const d = unwrapData<MeiliHit[]>(body as { data: MeiliHit[] });
          return Array.isArray(d) ? d : [];
        })
        .catch(() => [] as MeiliHit[]),
    ])
      .then(([f, m]) => {
        if (cancelled) return;
        setFed(f);
        setMeiliHits(Array.isArray(m) ? m : []);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced, token]);

  const suggestions = useMemo(() => buildSuggestions(fed, meiliHits), [fed, meiliHits]);

  const showPanel = menuOpen && debounced.length >= 1;

  useEffect(() => {
    if (!showPanel || suggestions.length === 0) {
      setActiveIndex(-1);
      return;
    }
    setActiveIndex((i) => (i >= 0 && i < suggestions.length ? i : 0));
  }, [showPanel, suggestions]);

  const goSearchPage = useCallback(() => {
    const t = q.trim();
    if (!t) return;
    setMenuOpen(false);
    navigate(`/search?q=${encodeURIComponent(t)}`);
  }, [navigate, q]);

  const activateSuggestion = useCallback(
    (s: Suggestion) => {
      navigate(s.to);
      setMenuOpen(false);
      setQ('');
    },
    [navigate],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setMenuOpen(false);
      inputRef.current?.blur();
      return;
    }

    if (e.key === 'ArrowDown') {
      if (!showPanel || suggestions.length === 0) return;
      e.preventDefault();
      setActiveIndex((i) => (i < 0 ? 0 : (i + 1) % suggestions.length));
      return;
    }

    if (e.key === 'ArrowUp') {
      if (!showPanel || suggestions.length === 0) return;
      e.preventDefault();
      setActiveIndex((i) => (i < 0 ? suggestions.length - 1 : (i - 1 + suggestions.length) % suggestions.length));
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      const highlighted = activeIndex >= 0 ? suggestions[activeIndex] : suggestions[0];
      if (highlighted) {
        activateSuggestion(highlighted);
      } else {
        goSearchPage();
      }
    }
  };

  const activeOptionId =
    showPanel && activeIndex >= 0 && suggestions[activeIndex]
      ? suggestionOptionId(suggestions[activeIndex])
      : undefined;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div className={styles.searchRow}>
        <SearchNormal1Twotone className={styles.icon} {...MX_NAV_ICON} aria-hidden />
        <input
          ref={inputRef}
          type="search"
          role="combobox"
          className={styles.input}
          placeholder="Rechercher…"
          aria-label="Rechercher dans l'admin"
          value={q}
          aria-autocomplete="list"
          aria-expanded={showPanel}
          aria-controls="nav-search-suggestions"
          aria-activedescendant={activeOptionId}
          autoComplete="off"
          onChange={(e) => {
            setQ(e.target.value);
            setMenuOpen(true);
          }}
          onFocus={() => setMenuOpen(true)}
          onKeyDown={onKeyDown}
        />
      </div>

      {showPanel ? (
        <div id="nav-search-suggestions" className={styles.dropdown} role="listbox" aria-label="Suggestions">
          {loading ? (
            <div className={styles.loading}>
              <Group gap="sm" align="center">
                <Loader />
                <Text size="sm" c="dimmed">
                  Recherche…
                </Text>
              </Group>
            </div>
          ) : suggestions.length === 0 ? (
            <div className={styles.empty}>Aucun résultat</div>
          ) : (
            suggestions.map((s, index) => (
              <Link
                key={`${s.kind}-${s.id}-${s.to}`}
                id={suggestionOptionId(s)}
                to={s.to}
                className={styles.row}
                role="option"
                aria-selected={index === activeIndex}
                data-active={index === activeIndex ? 'true' : undefined}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  setMenuOpen(false);
                  setQ('');
                }}
              >
                <EntityTypeMark entityType={s.entityType} />
                <span className={styles.label}>{s.label}</span>
              </Link>
            ))
          )}
          <div className={styles.footer}>
            <Link to={`/search?q=${encodeURIComponent(debounced)}`} onMouseDown={(e) => e.preventDefault()}>
              Voir tout dans Recherche →
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
